import { defineStore } from 'pinia'
import { Cart } from '@/types'
import { CART_DUMMY_ID } from '@/constants'
import { useNotifyStore } from '@/stores/notifications'
import { uuid } from '@/composables'
import { useAxios } from '@/composables/useAxios'
import { getBackendErrorCode, isCartLockedError, postCartWithBusyRetry } from '@/composables/useAxiosError'
import { cartRefusalKey } from '@/composables/useMailBilling'
import { forgetTransferCodes } from '@/composables/useDomainSearch'
import { computed, ref, toRaw, watch } from 'vue'
import { useJwtStore } from './jwt'
import { safeLocalStorage } from '@/composables/useSSR'
import { translate } from '@/i18n'

const CART_STORAGE_KEY = 'cart'
const CART_ID_STORAGE_KEY = 'cart_id'
const CART_OWNER_STORAGE_KEY = 'cart_owner'

// A transfer code is kept in memory only: the server stores it with the cart,
// and localStorage outlives the session on a shared browser.
const withoutTransferCodes = (cartData: Cart): Cart => ({
  ...cartData,
  items: cartData.items.map((item) => {
    if (item.meta_data?.authcode === undefined) return item
    const meta = { ...item.meta_data }
    delete meta.authcode
    return { ...item, meta_data: meta }
  })
})

const removeStoredCart = () => {
  const storage = safeLocalStorage()
  if (!storage) return
  storage.removeItem(CART_STORAGE_KEY)
  storage.removeItem(CART_ID_STORAGE_KEY)
  storage.removeItem(CART_OWNER_STORAGE_KEY)
}

const emptyCart = (): Cart => ({ id: CART_DUMMY_ID, items: [] })

export const useCartStore = defineStore('cart', () => {
  const cart = ref<Cart>(emptyCart())
  const isLoading = ref(false)
  const isInitialized = ref(false)
  const lastSyncError = ref<string | null>(null)
  // Backend error code of the last failed sync, so a refused item can say why.
  let lastSyncErrorCode: string | undefined
  // Flag to prevent race condition: when cart is cleared during checkout,
  // any in-flight fetchCart() should not restore the old cart
  const wasCleared = ref(false)

  // The account a cart is bound to: the one acted as, else the signed-in one.
  const callerId = (): string | null => {
    const jwt = useJwtStore().getJwtContent
    const id = jwt?.acting_as_account_id ?? jwt?.id
    return id === undefined ? null : String(id)
  }

  // A cart saved signed in belongs to that account, so anyone else on this
  // browser, signed out included, starts from an empty one. A cart saved
  // signed out has no owner and goes to whoever signs in.
  function dropAnotherAccountsCart() {
    const owner = safeLocalStorage()?.getItem(CART_OWNER_STORAGE_KEY) ?? null
    if (owner === null || owner === callerId()) return
    removeStoredCart()
    cart.value = emptyCart()
  }

  // The jwt store follows sign-in and sign-out in other tabs too, by which
  // time that tab may already have cleared the stored owner. So a cart held
  // for a previous account is replaced by whatever storage holds now, and
  // codes never carry over, those of the last search included: binding a
  // cart drops them on the server as well.
  watch(
    callerId,
    (_, previous) => {
      dropAnotherAccountsCart()
      cart.value = previous === null ? withoutTransferCodes(cart.value) : getLocalCart()
      forgetTransferCodes()
    },
    { flush: 'sync' }
  )

  // Load cart ID from localStorage (for persistence across sessions)
  const getStoredCartId = (): string => {
    const storage = safeLocalStorage()
    return storage?.getItem(CART_ID_STORAGE_KEY) || CART_DUMMY_ID
  }

  // Save cart ID to localStorage
  const storeCartId = (id: string) => {
    const storage = safeLocalStorage()
    if (storage) {
      storage.setItem(CART_ID_STORAGE_KEY, id)
    }
  }

  // Get local backup cart
  const getLocalCart = (): Cart => {
    try {
      const storage = safeLocalStorage()
      const stored = storage?.getItem(CART_STORAGE_KEY)
      if (stored) {
        const parsed = JSON.parse(stored)
        const validated = Cart.safeParse(parsed)
        if (validated.success) {
          return withoutTransferCodes(validated.data)
        }
      }
    } catch {
      // Ignore parse errors
    }
    return emptyCart()
  }

  // A cart stored before codes stayed in memory. Only the cart entry is
  // rewritten: stamping an owner here would bind a signed-out cart.
  try {
    const storage = safeLocalStorage()
    if (storage?.getItem(CART_STORAGE_KEY)?.includes('"authcode"')) {
      const local = getLocalCart()
      if (local.items.length > 0) storage.setItem(CART_STORAGE_KEY, JSON.stringify(local))
      else storage.removeItem(CART_STORAGE_KEY)
    }
  } catch {
    // Ignore storage errors
  }

  // Save cart to localStorage as backup
  const saveLocalCart = (cartData: Cart) => {
    try {
      const storage = safeLocalStorage()
      if (storage) {
        storage.setItem(CART_STORAGE_KEY, JSON.stringify(withoutTransferCodes(toRaw(cartData))))
        storeCartId(cartData.id)
        const owner = callerId()
        if (owner === null) storage.removeItem(CART_OWNER_STORAGE_KEY)
        else storage.setItem(CART_OWNER_STORAGE_KEY, owner)
      }
    } catch {
      // Ignore storage errors
    }
  }

  // GETTER
  const getCart = computed(() => cart.value)

  // Number of cart line items (not summed quantities). Single source of truth
  // for the cart badge across the nav, header, and floating button.
  const cartItemCount = computed(() => getCart.value.items?.length ?? 0)

  // Fetch cart from backend
  async function fetchCart(cartId?: string): Promise<boolean> {
    // If cart was cleared (e.g., after checkout), don't restore it
    if (wasCleared.value) {
      isInitialized.value = true
      return true
    }

    dropAnotherAccountsCart()
    const id = cartId || getStoredCartId()

    // Don't fetch if using dummy ID (new cart)
    if (id === CART_DUMMY_ID) {
      const localCart = getLocalCart()
      if (localCart.items.length > 0 && !wasCleared.value) {
        cart.value = localCart
      }
      isInitialized.value = true
      return true
    }

    isLoading.value = true
    lastSyncError.value = null
    const caller = callerId()
    // Cleared during the fetch, or fetched for an identity that has since
    // changed: what came back is not this cart any more.
    const superseded = () => wasCleared.value || callerId() !== caller

    try {
      const jwtStore = useJwtStore()
      const hasAuth = !!jwtStore.getJwt

      const response = await useAxios<void, Cart>('GET', `/cart/${id}`, hasAuth)

      if (superseded()) {
        isInitialized.value = true
        return true
      }

      if (response?.status === 200 && response.data) {
        const validated = Cart.safeParse(response.data)
        if (validated.success) {
          cart.value = validated.data
          saveLocalCart(validated.data)
          isInitialized.value = true
          return true
        }
      }

      // Cart not found or error - use local backup
      const localCart = getLocalCart()
      cart.value = localCart
      isInitialized.value = true
      return false
    } catch (error) {
      if (superseded()) {
        isInitialized.value = true
        return true
      }
      // Gone, or bound to an account other than the caller: start a new cart.
      const status = (error as { response?: { status?: number } }).response?.status
      if (status === 404 || status === 401 || status === 403) {
        cart.value = emptyCart()
        removeStoredCart()
        isInitialized.value = true
        return true
      }

      lastSyncError.value = 'Failed to fetch cart from server'
      // Fall back to local cart for other errors
      const localCart = getLocalCart()
      cart.value = localCart
      isInitialized.value = true
      return false
    } finally {
      isLoading.value = false
    }
  }

  // Sync cart to backend
  async function syncCart(cartData: Cart): Promise<Cart | null> {
    isLoading.value = true
    lastSyncError.value = null
    lastSyncErrorCode = undefined

    // The backend freezes a cart once a payment is attached to it, since its
    // items are what the webhook will fulfil. An edit after checkout has
    // started belongs in a new cart rather than being dropped. `payload` is
    // what gets saved locally on every failure path, including the outer catch,
    // so a locked id is never carried forward to be refused again on the next
    // mutation.
    let payload = toRaw(cartData)
    const caller = callerId()

    try {
      const jwtStore = useJwtStore()
      const hasAuth = !!jwtStore.getJwt

      let response
      try {
        response = await postCartWithBusyRetry(() =>
          useAxios<Cart, Cart>('POST', '/cart', hasAuth, payload)
        )
      } catch (error) {
        if (!isCartLockedError(error)) {
          throw error
        }
        payload = { ...payload, id: CART_DUMMY_ID }
        response = await useAxios<Cart, Cart>('POST', '/cart', hasAuth, payload)
      }

      // The identity changed mid-request: the store already holds the new
      // identity's cart, and this one must not be stored under it.
      if (callerId() !== caller) return null

      if (response?.status === 200 && response.data) {
        const validated = Cart.safeParse(response.data)
        if (validated.success) {
          // Backend may return updated cart (e.g., with new ID)
          cart.value = validated.data
          saveLocalCart(validated.data)
          return validated.data
        }
      }

      // Sync failed - save locally as backup
      saveLocalCart(payload)
      lastSyncError.value = 'Failed to sync cart to server'
      return null
    } catch (error) {
      lastSyncError.value = 'Failed to sync cart to server'
      lastSyncErrorCode = getBackendErrorCode(error)
      if (callerId() !== caller) return null
      if (lastSyncErrorCode === 'cart_owner_required') {
        // Another account's cart: nothing of it is kept or saved again.
        removeStoredCart()
        cart.value = emptyCart()
        return null
      }
      // Save locally as backup
      saveLocalCart(payload)
      return null
    } finally {
      isLoading.value = false
    }
  }

  // SETTER - updates cart and syncs to backend. Resolves false when the change
  // was refused and rolled back, so a caller can skip its own success message.
  async function setCart(payload: Cart, skipNotify = false): Promise<boolean> {
    const notifyStore = useNotifyStore()

    // Reset cleared flag when adding items - user is starting fresh cart
    if (payload.items.length > 0) {
      wasCleared.value = false
    }

    const validate = Cart.safeParse(payload)
    if (!validate.success) {
      console.error('Cart validation failed:', validate.error.format())
      notifyStore.createNotify({
        id: uuid(),
        alertType: 'error',
        title: 'Error',
        message: 'Payload needs to be an instance of Cart'
      })
      return false
    }

    // If cart is empty, reset to dummy ID and clear storage (no backend sync needed)
    if (validate.data.items.length === 0) {
      cart.value = emptyCart()
      removeStoredCart()
      return true
    }

    // Snapshot the last known-good state so we can roll back if the sync fails.
    // Deep-clone to avoid the snapshot aliasing the reactive cart we're about to mutate.
    const previousCart: Cart = structuredClone(toRaw(cart.value))
    const caller = callerId()

    // Update local state immediately for responsiveness
    cart.value = toRaw(validate.data)

    // Save locally as backup
    saveLocalCart(validate.data)

    // Sync to backend
    const syncedCart = await syncCart(validate.data)

    if (!syncedCart) {
      if (callerId() !== caller) return false
      // Sync failed: roll back the optimistic update so the badge/cart don't show
      // a phantom item, and surface the failure to the user. A cart refused as
      // another account's was already dropped and stays dropped.
      if (lastSyncErrorCode !== 'cart_owner_required') {
        cart.value = previousCart
        saveLocalCart(previousCart)
      }
      if (!skipNotify) {
        notifyStore.createNotify({
          id: uuid('v4'),
          alertType: 'error',
          title: translate('cart.saveError.title'),
          message: translate(cartRefusalKey(lastSyncErrorCode) ?? 'cart.saveError.message')
        })
      }
      return false
    }

    // If backend returned a different cart (e.g., new ID), update local state
    if (syncedCart.id !== validate.data.id) {
      cart.value = syncedCart
      saveLocalCart(syncedCart)
    }
    return true
  }

  function updateId(payload: string) {
    cart.value.id = payload
    storeCartId(payload)
  }

  async function clearCart() {
    // Set flag to prevent any in-flight fetchCart() from restoring the old cart
    wasCleared.value = true

    // Clear local state
    cart.value = emptyCart()

    // Clear localStorage
    removeStoredCart()

    // Note: We don't sync empty cart to backend because it rejects empty carts
    // The cart will be re-created on the backend when items are added
  }

  // Initialize cart - call this on app startup
  async function initCart() {
    if (isInitialized.value) return
    await fetchCart()
  }

  // Re-fetch cart (e.g., after login to get account-bound cart)
  async function refreshCart() {
    isInitialized.value = false
    await fetchCart()
  }

  return {
    cart,
    isLoading,
    isInitialized,
    lastSyncError,
    setCart,
    updateId,
    clearCart,
    getCart,
    initCart,
    refreshCart,
    fetchCart,
    syncCart,
    cartItemCount
  }
})
